import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TextField } from '@dsny/dsny-component-library'
import LoginCard from './LoginCard'
import AuthActions, { thunks as AuthThunks } from 'reducers/Auth'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { ForgotPasswordSchema } from '../authSchema'
import SuccessPage from './SuccessPage/SuccessPage'
import { ContentWrapper } from './SuccessPage/SuccessPage.styles'
import {
  Container,
  FormWrapper,
  FullWidthButton,
  Title
} from './AuthForm.styles'
import { FooterLink } from 'containers/Auth/Auth.styles'
import AuthLayout from '../AuthLayout'
import AuthRolloutBoundary from '../LoginWithRollout'

export default function ForgotPassword({
  responsiveBreakpointsPage = RESPONSIVE_BREAKPOINT_PAGES.FORGOT_PASSWORD,
  responsiveBreakpointsRollout = false
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [forgotPasswordRequested, setForgotPasswordRequested] = useState(false)
  const [isRequestResent, setIsRequestResent] = useState(false)
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(ForgotPasswordSchema)
  })

  const email = getValues('email')

  const showForgotLayout = () => {
    setForgotPasswordRequested(false)
    setIsRequestResent(false)
  }

  const onSubmit = async (value) => {
    try {
      await dispatch(
        AuthThunks.requestForgotPwd({
          email: value.email
        })
      )
      setForgotPasswordRequested(true)
    } catch (error) {
      console.error(error?.data)
    }
  }

  const handleResendRequest = async () => {
    setIsRequestResent(true)
    await onSubmit({ email })
  }

  const content = (
    <AuthLayout>
      <LoginCard>
        {forgotPasswordRequested ? (
          <>
            <SuccessPage
              dataCy="forgot-password-success"
              title={t('login.password-reset-requested')}
              onSubmit={handleResendRequest}
              returnSignInAction={() => {
                navigate('/')
              }}
              forgotPasswordLayout={true}
              isRequestResent={isRequestResent}
              showForgotLayout={showForgotLayout}
            >
              <ContentWrapper>
                {t('login.password-reset-content-one')}
                {` `}
                <span style={{ fontWeight: 700 }}>[{email}]</span>
                {` `}
                {t('login.password-reset-content-two')}
              </ContentWrapper>

              <ContentWrapper>
                {t('login.password-reset-content-three')}
                {` `}
                <span style={{ fontWeight: 700 }}>{'support@plaympe.com'}</span>
              </ContentWrapper>
            </SuccessPage>
          </>
        ) : (
          <Container>
            <FormWrapper>
              <Title>{t('login.forgot-password-header')}</Title>

              <form
                data-cy="forgot-password-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div data-cy="forgot-password-email">
                  <TextField
                    id="email"
                    name="email"
                    required
                    {...register('email')}
                    setting="player"
                    ref={null}
                    inputRef={register('email').ref}
                    wrapperStyles={{ paddingRight: 0, marginBottom: 32 }}
                    labelText={t('signup.email')}
                    error={errors?.email?.message}
                    borderColor={theme.colors.neutralW50}
                    highlightColor={theme.colors.interact70}
                    inputStyle={{
                      borderColor: errors?.email && theme.colors.error70
                    }}
                    errorStyle={{ color: theme.colors.error70, marginTop: 8 }}
                  />
                </div>
                <FullWidthButton
                  data-cy="forgot-password-submit"
                  type="submit"
                  color="primary"
                  setting="player"
                >
                  {t('login.send-password-reset-email')}
                </FullWidthButton>
              </form>

              <FooterLink
                style={{ textAlign: 'center' }}
                onClick={() => {
                  navigate('/')
                }}
              >
                <ContentWrapper>
                  {t('login.return-to')}
                  {` `}
                  <span
                    style={{ fontWeight: 700, color: theme.colors.interact70 }}
                  >
                    {t('login.sign-in')}
                  </span>
                  {t('login.page')}
                </ContentWrapper>
              </FooterLink>
            </FormWrapper>
          </Container>
        )}
      </LoginCard>
    </AuthLayout>
  )

  return responsiveBreakpointsRollout ? (
    <AuthRolloutBoundary page={responsiveBreakpointsPage}>
      {content}
    </AuthRolloutBoundary>
  ) : (
    content
  )
}

ForgotPassword.propTypes = {
  responsiveBreakpointsPage: PropTypes.string,
  responsiveBreakpointsRollout: PropTypes.bool
}
